import React from 'react';
import { 
  X, 
  User, 
  Briefcase, 
  Calendar, 
  MapPin, 
  Mail, 
  Phone,
  Star,
  TrendingUp,
  Award,
  Building,
  FileText,
  Clock
} from 'lucide-react';

const SubmissionViewModal = ({ isOpen, onClose, submission }) => {
  if (!isOpen || !submission) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'sourced': 'bg-blue-100 text-blue-800',
      'screened': 'bg-purple-100 text-purple-800',
      'submitted': 'bg-yellow-100 text-yellow-800',
      'client_reviewing': 'bg-orange-100 text-orange-800',
      'client_accepted': 'bg-green-100 text-green-800',
      'client_rejected': 'bg-red-100 text-red-800',
      'interview_scheduled': 'bg-indigo-100 text-indigo-800',
      'interview_passed': 'bg-green-100 text-green-800',
      'interview_failed': 'bg-red-100 text-red-800',
      'offer_made': 'bg-emerald-100 text-emerald-800',
      'offer_extended': 'bg-emerald-100 text-emerald-800',
      'offer_accepted': 'bg-green-100 text-green-800',
      'offer_rejected': 'bg-red-100 text-red-800',
      'onboarding': 'bg-blue-100 text-blue-800',
      'placed': 'bg-green-100 text-green-800',
      'withdrawn': 'bg-gray-100 text-gray-800',
      'rejected': 'bg-red-100 text-red-800'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';
    const displayText = status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
        {displayText}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex justify-between items-start">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white mb-1">Submission Details</h2>
            <p className="text-blue-100 text-sm">View submission information</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Status and Key Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Current Status</h3>
                {getStatusBadge(submission.status || submission.current_status)}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Submitted Date</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(submission.submitted_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Submitted By</p>
                  <p className="text-sm font-medium text-gray-900">{submission.submitted_by_name || submission.recruiter_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Updated Date</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(submission.updated_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Submission ID</p>
                  <p className="text-sm font-medium text-gray-900">{submission.id?.slice(0, 8) || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Candidate Information */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Candidate Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Name</label>
                  <p className="font-medium text-gray-900">{submission.candidate_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Email</label>
                  <p className="font-medium text-gray-900 flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    {submission.candidate_email || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Phone</label>
                  <p className="font-medium text-gray-900 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    {submission.candidate_phone || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Location</label>
                  <p className="font-medium text-gray-900 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    {submission.candidate_location || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Job Information */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-purple-600" />
                Job Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Job Title</label>
                  <p className="font-medium text-gray-900">{submission.job_title || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Client</label>
                  <p className="font-medium text-gray-900 flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-400" />
                    {submission.client_name || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Location</label>
                  <p className="font-medium text-gray-900">{submission.job_location || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Job ID</label>
                  <p className="font-medium text-gray-900">{submission.job_id?.slice(0, 8) || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* AI Match Scores */}
            {(submission.ai_match_score || submission.match_score || submission.match_breakdown) && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-purple-50 to-blue-50">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="h-5 w-5 text-purple-600" />
                  AI Match Analysis
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {submission.ai_match_score && (
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Overall Match</p>
                      <p className="text-2xl font-bold text-purple-600">{Math.round(submission.ai_match_score)}%</p>
                    </div>
                  )}
                  {submission.match_breakdown?.skill_match && (
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Skills Match</p>
                      <p className="text-2xl font-bold text-blue-600">{Math.round(submission.match_breakdown.skill_match)}%</p>
                    </div>
                  )}
                  {submission.match_breakdown?.experience_match && (
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Experience</p>
                      <p className="text-2xl font-bold text-indigo-600">{Math.round(submission.match_breakdown.experience_match)}%</p>
                    </div>
                  )}
                  {submission.match_breakdown?.location_match && (
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Location</p>
                      <p className="text-2xl font-bold text-green-600">{Math.round(submission.match_breakdown.location_match)}%</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Compensation Details */}
            {(submission.pay_rate || submission.bill_rate || submission.salary_expectation) && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Compensation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {submission.pay_rate && (
                    <div>
                      <label className="text-sm text-gray-600">Pay Rate</label>
                      <p className="font-medium text-gray-900">${submission.pay_rate}/hr</p>
                    </div>
                  )}
                  {submission.bill_rate && (
                    <div>
                      <label className="text-sm text-gray-600">Bill Rate</label>
                      <p className="font-medium text-gray-900">${submission.bill_rate}/hr</p>
                    </div>
                  )}
                  {submission.salary_expectation && (
                    <div>
                      <label className="text-sm text-gray-600">Salary Expectation</label>
                      <p className="font-medium text-gray-900">${submission.salary_expectation}/year</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes and Comments */}
            {submission.submission_notes && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-gray-600" />
                  Notes
                </h3>
                <p className="text-gray-700 whitespace-pre-wrap">{submission.submission_notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmissionViewModal;
